import { useEffect, useRef, useState } from "react";
import { useEncrypt } from "../../../hooks/useEncrypt";
import apiParticipantes from "../../../api/Participantes/api-participantes";
import {
  ResumenAvisos,
  avisosDeLaPostulacion,
  fechaDelPrimerInforme,
  fechaLimiteParaSubsanar,
} from "../../../utils/avisosPostulacion";

/**
 * Lo que hay guardado de la postulacion que se esta armando, y los avisos que
 * salen de ello.
 *
 * Se vuelve a leer cada vez que cambia la revision —el wizard le pasa el
 * paso— porque los documentos se cargan dentro de sus propios paneles y desde
 * aqui no hay forma de enterarse de otro modo.
 *
 * Lo que advierte es siempre lo del primer informe: la propuesta se crea y
 * se edita aqui, y la subsanacion va por su propio camino.
 */
export const useAvisosPostulacion = (
  convocatoria: any,
  revision: any,
  /** El perfil elegido en el wizard, 1..3; manda sobre lo guardado. */
  tipoParticipante?: number | null
) => {
  const { decrypt } = useEncrypt();
  const [postulacion, setPostulacion] = useState<any>(null);
  const [recargando, setRecargando] = useState<boolean>(true);
  const montado = useRef(true);

  useEffect(
    () => () => {
      montado.current = false;
    },
    []
  );

  const recargar = async () => {
    const id = decrypt(localStorage.getItem("id_postulacion"));
    if (!id) {
      setRecargando(false);
      return;
    }

    setRecargando(true);

    try {
      const respuesta: any = await apiParticipantes.consultarPostulacion(id);
      const cuerpo = respuesta?.data ?? respuesta;
      const guardada = Array.isArray(cuerpo) ? cuerpo[0] : cuerpo;
      if (!montado.current) return;
      setPostulacion(guardada ?? {});
    } catch (error) {
      // Si no se puede leer, la postulacion sigue: se queda sin avisos, que
      // es como se veia antes de que existieran.
      console.error("No se pudieron calcular los avisos de la postulación", error);
    } finally {
      if (montado.current) setRecargando(false);
    }
  };

  useEffect(() => {
    recargar();
  }, [revision]);

  // Los documentos se guardan contando desde cero; el perfil, desde uno.
  const elegido = tipoParticipante ?? Number(postulacion?.tipo_participante);
  const tipoPersona = Number.isFinite(elegido) && elegido > 0 ? elegido - 1 : null;

  const resumen: ResumenAvisos = avisosDeLaPostulacion(
    postulacion?.documentos_administrativos,
    postulacion?.documentos_tecnicos,
    tipoPersona
  );

  return {
    /** Lo guardado tal como vino, para el recuento previo a inscribir */
    postulacion,
    resumen,
    fechaDelInforme: fechaDelPrimerInforme(convocatoria?.fechas),
    fechaLimiteSubsanar: fechaLimiteParaSubsanar(convocatoria?.fechas),
    /**
     * Mientras no se sepa que hay guardado no se afirma nada, en ningun
     * sentido. Despues ya no se vuelve a vaciar: al releer se deja en
     * pantalla lo anterior hasta que llega lo nuevo, para que los avisos no
     * parpadeen cada vez que se cambia de paso.
     */
    cargando: postulacion === null,
    /**
     * Hay una lectura en curso. Mientras dure no se afirma nada: al
     * cambiar de perfil los documentos guardados son todavia los del
     * anterior, y filtrarlos por el nuevo daba cero avisos y un cartel
     * verde que se desmentia solo un instante despues.
     */
    recargando,
    recargar,
  };
};
